import { useEffect, useState } from "react";
import { CircleDot } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import DataTable from "../components/DataTable";
import StatCard from "../components/StatCard";
import TireDialog from "../components/TireDialog";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { fmtDate, fmtMoney, fmtNum, todayISO } from "../utils/format";

export default function Tires() {
  const [rows, setRows] = useState([]);
  const [removeFor, setRemoveFor] = useState(null);
  const [removeForm, setRemoveForm] = useState({ removed_date: todayISO(), removed_mileage: "" });

  const load = () => api.get("/tires").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const inService = rows.filter((t) => !t.removed_date);
  const totalCost = rows.reduce((s, t) => s + (t.price || 0), 0);

  const submitRemove = async () => {
    try {
      await api.post(`/tires/${removeFor.id}/remove`, { ...removeForm, removed_mileage: removeForm.removed_mileage ? Number(removeForm.removed_mileage) : null });
      toast.success("Remplacement enregistré");
      setRemoveFor(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="tires-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Pneus</h1>
          <p className="mt-1 text-sm text-slate-500">Suivi des pneus par véhicule — coût/km calculé au remplacement.</p>
        </div>
        <TireDialog onSaved={load} />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard icon={CircleDot} label="Trains en service" value={inService.length} tone="cyan" testid="tires-in-service" />
        <StatCard icon={CircleDot} label="Total enregistré" value={rows.length} tone="slate" testid="tires-total" />
        <StatCard icon={CircleDot} label="Coût total" value={fmtMoney(totalCost)} tone="amber" testid="tires-cost" />
      </div>

      <DataTable testidPrefix="tires-table" rows={rows}
        searchKeys={["vehicle_label", "brand", "dimension", "position"]}
        columns={[
          { key: "vehicle_label", label: "Véhicule", render: (r) => <span className="font-medium">{r.vehicle_label}</span> },
          { key: "brand", label: "Marque" },
          { key: "dimension", label: "Dimension", render: (r) => r.dimension || "—" },
          { key: "quantity", label: "Qté", className: "text-center" },
          { key: "price", label: "Prix", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.price)}</span> },
          { key: "mount_date", label: "Montage", render: (r) => r.mount_date ? `${fmtDate(r.mount_date)} / ${fmtNum(r.mount_mileage)} km` : "—" },
          { key: "position", label: "Position", render: (r) => r.position || "—" },
          { key: "km_done", label: "Km parcourus", className: "text-right", render: (r) => r.km_done != null ? <span className="font-mono-num">{fmtNum(r.km_done)}</span> : <span className="text-emerald-600 text-xs font-semibold">En service</span> },
          { key: "cost_per_km", label: "Coût/km", className: "text-right", render: (r) => r.cost_per_km != null ? <span className="font-mono-num">{fmtNum(r.cost_per_km)} FCFA</span> : "—" },
          { key: "actions", label: "", sortable: false, render: (r) => !r.removed_date && (
            <Button size="sm" variant="outline" data-testid={`remove-tire-${r.id}`} onClick={() => { setRemoveFor(r); setRemoveForm({ removed_date: todayISO(), removed_mileage: "" }); }}>Remplacer</Button>
          ) },
        ]}
      />

      <Dialog open={!!removeFor} onOpenChange={() => setRemoveFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remplacement — {removeFor?.brand} {removeFor?.dimension}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date de remplacement</Label><Input type="date" value={removeForm.removed_date} onChange={(e) => setRemoveForm({ ...removeForm, removed_date: e.target.value })} data-testid="tire-remove-date" /></div>
            <div><Label>Kilométrage au remplacement</Label><Input type="number" value={removeForm.removed_mileage} onChange={(e) => setRemoveForm({ ...removeForm, removed_mileage: e.target.value })} data-testid="tire-remove-km" /></div>
          </div>
          <DialogFooter><Button onClick={submitRemove} data-testid="tire-remove-submit">Valider</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
